import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { Reveal, EASE, fadeInUp } from '@/components/landing/motion';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

type Cell = string | { good: boolean; label: string };

interface Row {
  label: string;
  cells: Cell[];
}

export function ComparePage() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: 'Compare — Knowlez vs INDX, Glean, Onyx, Coveo, M365 Copilot',
    description: 'How Knowlez stacks up against INDX, Glean, Onyx, Coveo, and M365 Copilot on JP-deep ingestion, knowledge graph, on-prem option, and public per-seat pricing.',
    path: '/compare',
  });

  const headers = ['Knowlez', 'INDX', 'Glean', 'Onyx', 'Coveo', 'M365 Copilot'];

  const rows: Row[] = [
    {
      label: t('compare.row.pricing', { defaultValue: 'Public per-seat pricing' }) as string,
      cells: [
        { good: true, label: '$24 / $59 / $129' },
        { good: false, label: 'Contact sales' },
        { good: false, label: 'Contact sales (~$50/seat, 100-seat min)' },
        { good: true, label: '$20 / seat' },
        { good: false, label: 'Contact sales' },
        { good: true, label: '$30 / seat (add-on)' },
      ],
    },
    {
      label: t('compare.row.deploy', { defaultValue: 'Cloud OR on-prem' }) as string,
      cells: [
        { good: true, label: 'Both, same codebase' },
        { good: false, label: 'On-prem only' },
        { good: false, label: 'Cloud only' },
        { good: true, label: 'Cloud + OSS self-host' },
        { good: false, label: 'Cloud-first' },
        { good: false, label: 'Cloud only' },
      ],
    },
    {
      label: t('compare.row.jp_ingest', { defaultValue: 'JP-deep ingestion (fax · handwriting · hanko · 議事録)' }) as string,
      cells: [
        { good: true, label: 'Native VLM routing' },
        { good: true, label: 'On-prem only' },
        { good: false, label: 'None' },
        { good: false, label: 'None' },
        { good: false, label: 'None' },
        { good: false, label: 'None' },
      ],
    },
    {
      label: t('compare.row.connectors', { defaultValue: 'Global SaaS + JP connectors' }) as string,
      cells: [
        { good: true, label: '11 global + 3 JP (kintone · Chatwork · LINE WORKS)' },
        { good: false, label: 'Engine only' },
        { good: true, label: '100+ global, no JP-native' },
        { good: true, label: '~40 global, no JP-native' },
        { good: true, label: 'Many global, no JP-native' },
        { good: false, label: 'M365 + a few' },
      ],
    },
    {
      label: t('compare.row.hybrid_rag', { defaultValue: 'Hybrid RAG (vector + BM25 + graph)' }) as string,
      cells: [
        { good: true, label: 'RRF · 3 lanes · reranked' },
        { good: false, label: 'Engine output, customer wires RAG' },
        { good: true, label: 'Yes (cloud only)' },
        { good: true, label: 'Yes (OSS)' },
        { good: true, label: 'Yes' },
        { good: false, label: 'Closed' },
      ],
    },
    {
      label: t('compare.row.permission', { defaultValue: 'Source-system ACL at retrieval' }) as string,
      cells: [
        { good: true, label: 'Re-checked per query' },
        { good: false, label: 'Customer responsibility' },
        { good: true, label: 'Re-checked per query' },
        { good: false, label: 'Best-effort' },
        { good: true, label: 'Yes' },
        { good: true, label: 'Inside M365 only' },
      ],
    },
    {
      label: t('compare.row.model_choice', { defaultValue: 'Model choice' }) as string,
      cells: [
        { good: true, label: 'Pick + rotate per workspace' },
        { good: false, label: 'Closed' },
        { good: false, label: 'Closed' },
        { good: true, label: 'Configurable' },
        { good: false, label: 'Closed' },
        { good: false, label: 'Single-vendor' },
      ],
    },
    {
      label: t('compare.row.cost_visibility', { defaultValue: 'Per-query token + cost visibility' }) as string,
      cells: [
        { good: true, label: 'Shown on every answer' },
        { good: false, label: 'Closed' },
        { good: false, label: 'Closed' },
        { good: true, label: 'Yes' },
        { good: false, label: 'Closed' },
        { good: false, label: 'Closed' },
      ],
    },
    {
      label: t('compare.row.agents', { defaultValue: 'Agents (Search · Compose · Compare · Brief · Audit · Workflow)' }) as string,
      cells: [
        { good: true, label: 'All 6' },
        { good: false, label: 'Engine only' },
        { good: true, label: 'Search + chat' },
        { good: true, label: 'Search + chat' },
        { good: false, label: 'Search only' },
        { good: true, label: 'Search + draft' },
      ],
    },
    {
      label: t('compare.row.time_to_value', { defaultValue: 'Time to first cited answer' }) as string,
      cells: [
        { good: true, label: '< 15 minutes' },
        { good: false, label: '3 months' },
        { good: false, label: '4–8 weeks' },
        { good: true, label: '< 1 hour' },
        { good: false, label: 'Weeks' },
        { good: false, label: 'Hours (M365 only)' },
      ],
    },
    {
      label: t('compare.row.i18n', { defaultValue: 'Languages with RTL' }) as string,
      cells: [
        { good: true, label: '14 (incl. ar · ur)' },
        { good: false, label: 'JP-only UI' },
        { good: false, label: 'EN-first, JP lags' },
        { good: false, label: 'EN-only' },
        { good: true, label: 'Many EN-first' },
        { good: true, label: 'Many' },
      ],
    },
  ];

  const reduce = useReducedMotion();
  const rowVariant: Variants | undefined = reduce ? undefined : { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } };

  return (
    <div className="min-h-screen paper-archive">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-5 sm:px-6 pt-16 pb-24">
        <Reveal>
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.18em] text-accent-600 font-semibold">
              {t('compare.eyebrow', { defaultValue: 'Honest comparison' })}
            </p>
            <h1 className="mt-3 display serif-jp text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 leading-[1.05] text-balance">
              {t('compare.title', { defaultValue: 'Compare, honestly.' })}
            </h1>
            <p className="mt-4 text-[16px] text-ink-600 leading-relaxed text-pretty">
              {t('compare.subtitle', {
                defaultValue:
                  'Every competitor row is researched. Where INDX or Glean or Onyx beats us, we say so. Where we beat them — JP-deep ingestion, hybrid deployment, model choice, transparent pricing — we say that too.',
              })}
            </p>
          </div>
        </Reveal>

        <div className="mt-12 overflow-x-auto card-paper">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100">
                <th className="py-3 pl-5 pr-3 font-medium" />
                {headers.map((h, i) => (
                  <th
                    key={h}
                    className={`py-3 px-3 display ${i === 0 ? 'font-semibold text-accent-700 bg-accent-50/50' : 'font-semibold text-ink-900'}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <motion.tbody
              initial={reduce ? undefined : 'hidden'}
              whileInView={reduce ? undefined : 'visible'}
              viewport={{ once: true, margin: '-40px' }}
              variants={reduce ? undefined : { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
            >
              {rows.map((row) => (
                <motion.tr key={row.label} variants={rowVariant} transition={{ duration: 0.35, ease: EASE }} className="border-b border-ink-50 align-top">
                  <td className="py-3 pl-5 pr-3 font-medium text-ink-900">{row.label}</td>
                  {row.cells.map((cell, i) => {
                    if (typeof cell === 'string') {
                      return (
                        <td key={i} className="py-3 px-3 text-ink-700">
                          {cell}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={i}
                        className={`py-3 px-3 ${i === 0 ? 'bg-accent-50/30' : ''} ${cell.good ? 'text-emerald-700' : 'text-ink-500'}`}
                      >
                        <span className="mr-1">{cell.good ? '✓' : '○'}</span>
                        {cell.label}
                      </td>
                    );
                  })}
                </motion.tr>
              ))}
            </motion.tbody>
          </table>
        </div>

        <p className="mt-6 text-[12px] text-ink-500 max-w-3xl leading-relaxed">
          {t('compare.footnote', {
            defaultValue:
              'Prices reflect publicly published rates or Vendr-disclosed medians as of 2026-05. INDX = ¥-range, opaque, on-prem only. Glean median Vendr contract $97,500/yr at ~$50/seat with a 100-seat minimum. M365 Copilot is $30/seat as an add-on to M365.',
          })}
        </p>

        <Reveal variant={fadeInUp}>
          <div className="mt-14 card-paper p-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="display text-xl font-semibold text-ink-900">
                {t('compare.cta.headline', { defaultValue: 'Pick the cloud that names its price.' })}
              </h3>
              <p className="mt-1.5 text-[13.5px] text-ink-600">
                {t('compare.cta.body', { defaultValue: 'No 100-seat minimum. Free forever for teams under 10.' })}
              </p>
            </div>
            <Link to="/signup" className="btn-press inline-flex items-center px-5 py-3">
              {t('compare.cta.start', { defaultValue: 'Start free →' })}
            </Link>
          </div>
        </Reveal>
      </main>
      <SiteFooter />
    </div>
  );
}
